import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";

import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import { SelectEntity } from "@knowfully/ui.common";
import { DatePicker } from "@mui/x-date-pickers";
import {
  SaveContractRequest,
  IContract,
  ISaveContractRequest,
  IOrganizationDetails,
  ListResponseOfContractStatus,
  ListResponseOfContractType,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import Field from "components/Field";
import useGlobalContext from "hooks/globalData";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import { useStrictQuery } from "hooks/useStrictQuery";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<ISaveContractRequest>();

type OrganizationContractFormProps = {
  onCancel?: () => void;
  onContractSaved: () => void;
  organization: IOrganizationDetails;
  contract?: IContract;
  submitLabel: string;
};

const OrganizationContractForm = ({
  onCancel,
  onContractSaved,
  organization,
  contract,
  submitLabel,
}: OrganizationContractFormProps) => {
  const { formatMessage: t } = useIntl();
  const { contractService } = useGlobalContext();

  const styles = useStyles(useTheme());
  const init = {
    id: contract?.id,
    organizationId: organization.id,
    renewalDate: contract?.renewalDate,
    contractTypeId: contract?.contractTypeId,
    contractTypeName: contract?.contractTypeName,
    contractStatusId: contract?.contractStatusId,
    contractStatusName: contract?.contractStatusName,
  };

  const fetchedTypes = useStrictQuery<ListResponseOfContractType>(
    ["contractService.getContractTypes", organization.id],
    () => contractService.getContractTypes(new ListRequestOfBaseFilter())
  );

  const fetchedStatuses = useStrictQuery<ListResponseOfContractStatus>(
    ["contractService.getContractStatuses", organization.id],
    () => contractService.getContractStatuses(new ListRequestOfBaseFilter())
  );

  const onSubmit = async (values: ISaveContractRequest) => {
    try {
      await contractService.saveContract(new SaveContractRequest(values));

      onContractSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="contractTypeId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      variant="standard"
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectEntity
                        fetchedData={fetchedTypes}
                        labelField="name"
                        label={t({ id: "contractType" })}
                        {...input}
                        required={true}
                        textFieldProps={{
                          variant: "standard",
                          error: meta.error && meta.submitFailed,
                          required: true,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Field name="renewalDate" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <DatePicker
                      {...input}
                      label={t({ id: "renewalDate" })}
                      format="MM/dd/yyyy"
                      value={input.value === "" ? null : input.value}
                      slotProps={{
                        actionBar: {
                          actions: ["clear", "cancel"],
                        },
                        textField: {
                          variant: "standard",
                          fullWidth: true,
                          error:
                            (meta.touched || meta.submitFailed) && meta.error,
                          helperText:
                            (meta.touched || meta.submitFailed) && meta.error,
                        },
                      }}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="contractStatusId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      variant="standard"
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectEntity
                        fetchedData={fetchedStatuses}
                        labelField="name"
                        label={t({ id: "statusLabel" })}
                        {...input}
                        required={true}
                        textFieldProps={{
                          variant: "standard",
                          error: meta.error && meta.submitFailed,
                          required: true,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button type="submit" color="primary" disabled={submitting}>
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default OrganizationContractForm;
